import type { OnboardingPageDraft, SiteOnboardingDraft, SiteTypeChoice } from './onboarding-types';

export const DEFAULT_GENERATION_MODEL_ID = 'gemini-2.5-flash';
export const DEFAULT_IMAGE_MODEL_ID = 'imagen-3.0-generate-002';

const DEFAULT_PAGES: OnboardingPageDraft[] = [
  {
    title: 'Home',
    type: 'Home',
    description: 'Main landing page with hero, value proposition, and call to action.',
    attachments: [],
    verbosity: 'medium',
  },
];

export function emptySiteOnboardingDraft(): SiteOnboardingDraft {
  return {
    businessName: '',
    industry: '',
    description: '',
    services: '',
    phone: '',
    mission: '',
    vision: '',
    vibeTone: 50,
    vibeStyle: 50,
    customUrls: [],
    siteType: 'single',
    pagesConfig: DEFAULT_PAGES.map((p) => ({ ...p })),
    migrationTargetUrl: '',
    migrationUseContent: false,
    migrationUseImages: false,
    migrationCloneDesign: false,
    useNeutralImages: false,
    generationModelId: DEFAULT_GENERATION_MODEL_ID,
    imageModelId: DEFAULT_IMAGE_MODEL_ID,
  };
}

function clampInt(value: number, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Shallow merge of onboarding draft patch from chat API. */
export function mergeSiteOnboardingDraft(
  prev: SiteOnboardingDraft,
  patch: Partial<SiteOnboardingDraft>,
): SiteOnboardingDraft {
  const next: SiteOnboardingDraft = { ...prev, pagesConfig: [...prev.pagesConfig] };

  if (typeof patch.businessName === 'string' && patch.businessName.trim()) {
    next.businessName = patch.businessName.trim();
  }
  if (typeof patch.industry === 'string' && patch.industry.trim()) {
    next.industry = patch.industry.trim();
  }
  if (typeof patch.description === 'string' && patch.description.trim()) {
    next.description = patch.description.trim();
  }
  if (typeof patch.services === 'string' && patch.services.trim()) {
    next.services = patch.services.trim();
  }
  if (typeof patch.phone === 'string' && patch.phone.trim()) {
    next.phone = patch.phone.trim();
  }
  if (typeof patch.mission === 'string' && patch.mission.trim()) {
    next.mission = patch.mission.trim();
  }
  if (typeof patch.vision === 'string' && patch.vision.trim()) {
    next.vision = patch.vision.trim();
  }
  if (typeof patch.vibeTone === 'number') {
    next.vibeTone = clampInt(patch.vibeTone, 0, 100, prev.vibeTone);
  }
  if (typeof patch.vibeStyle === 'number') {
    next.vibeStyle = clampInt(patch.vibeStyle, 0, 100, prev.vibeStyle);
  }
  if (Array.isArray(patch.customUrls)) {
    next.customUrls = patch.customUrls.filter((u): u is string => typeof u === 'string' && u.trim().length > 0);
  }
  if (patch.siteType === 'single' || patch.siteType === 'multi' || patch.siteType === 'store') {
    next.siteType = patch.siteType;
  }
  if (Array.isArray(patch.pagesConfig) && patch.pagesConfig.length > 0) {
    next.pagesConfig = patch.pagesConfig;
  }

  return next;
}

/** Seed draft from home prompt before onboarding chat refines fields. */
export function seedDraftFromPrompt(query: string, siteType: SiteTypeChoice = 'single'): SiteOnboardingDraft {
  const draft = emptySiteOnboardingDraft();
  draft.description = query.trim();
  draft.mission = query.trim();
  draft.industry = 'General';
  draft.businessName = inferBusinessName(query);
  draft.siteType = siteType;
  return draft;
}

function inferBusinessName(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return 'New Project';
  const forMatch = trimmed.match(/\bfor\s+(?:a|an|the)?\s*(.+?)(?:\.|$)/i);
  if (forMatch?.[1]) {
    return forMatch[1].trim().slice(0, 80);
  }
  return trimmed.slice(0, 60);
}
